// src/services/authService.js (修正版本 - 解決循環依賴)
import axios from 'axios';

class AuthService {
  constructor() {
    this.debug = process.env.NODE_ENV === 'development';
    this.defaultCompanyId = '2';
    
    // 🔥 修復：創建專用的 auth API 客戶端，避免循環依賴
    this.authClient = null;
    this.initAuthClient();
  }

  /**
   * 🔥 修復：初始化認證客戶端，避免循環依賴
   */
  initAuthClient() {
    // 動態確定 API URL
    const getApiUrl = () => {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:5001/api';
      }
      
      return `${protocol}//${hostname}/api`;
    };

    this.authClient = axios.create({
      baseURL: getApiUrl(),
      timeout: 30000,
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // 添加請求攔截器
    this.authClient.interceptors.request.use(
      config => {
        const token = this.getToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        this.log('🔐 Auth request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          hasAuth: !!config.headers['Authorization']
        });
        
        return config;
      },
      error => {
        console.error('❌ Auth request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // 添加響應攔截器
    this.authClient.interceptors.response.use(
      response => {
        this.log('✅ Auth response success:', {
          status: response.status,
          url: response.config?.url
        });
        return response;
      },
      error => {
        console.error('❌ Auth response error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message
        });
        return Promise.reject(error);
      }
    );
  }

  /**
   * 開發環境記錄
   */
  log(...args) {
    if (this.debug) {
      console.log('[AuthService]', ...args);
    }
  }

  /**
   * 獲取當前用戶信息
   */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        this.log('本地存儲中找不到用戶');
        return null;
      }
      
      const user = JSON.parse(userStr);
      this.log('獲取到用戶信息:', { id: user.id, email: user.email, role: user.role });
      return user;
    } catch (error) {
      console.error('解析用戶信息錯誤:', error);
      return null;
    }
  }

  /**
   * 獲取當前用戶的公司ID
   */
  getCompanyId() {
    try {
      const user = this.getCurrentUser();
      
      // 1. 從用戶對象中獲取 (camelCase)
      if (user && user.companyId) {
        this.log('從用戶對象 (companyId) 獲取公司 ID:', user.companyId);
        return String(user.companyId);
      }
      
      // 2. 從用戶對象中獲取 (snake_case)
      if (user && user.company_id) {
        this.log('從用戶對象 (company_id) 獲取公司 ID:', user.company_id);
        return String(user.company_id);
      }
      
      // 3. 從 localStorage 直接獲取
      const companyId = localStorage.getItem('companyId');
      if (companyId) {
        this.log('從 localStorage 獲取公司 ID:', companyId);
        return String(companyId);
      }
      
      // 4. 使用默認值
      this.log('使用默認公司 ID:', this.defaultCompanyId);
      return String(this.defaultCompanyId);
    } catch (error) {
      console.error('獲取公司 ID 錯誤，使用默認值:', error);
      return String(this.defaultCompanyId);
    }
  }

  /**
   * 獲取認證令牌
   */
  getToken() {
    const tokenKeys = ['authToken', 'token', 'accessToken', 'jwt', 'jwtToken'];
    
    for (const key of tokenKeys) {
      const token = localStorage.getItem(key);
      if (token) {
        // 不要在生產環境中記錄完整的 token
        this.log(`從 ${key} 獲取到令牌 (長度: ${token.length})`);
        return token;
      }
    }
    
    this.log('無法找到有效的認證令牌');
    return null;
  }

  /**
   * 檢查用戶是否已登入
   */
  isLoggedIn() {
    const hasToken = !!this.getToken();
    const hasUser = !!this.getCurrentUser();
    const isLoggedIn = hasToken && hasUser;
    
    this.log('用戶登入狀態:', {
      hasToken,
      hasUser,
      isLoggedIn
    });
    
    return isLoggedIn;
  }

  /**
   * 保存令牌
   */
  saveToken(token) {
    if (!token) {
      console.warn('嘗試保存空令牌');
      return;
    }
    
    localStorage.setItem('authToken', token);
    this.log('令牌已保存到 localStorage');
  }

  /**
   * 🔥 修復：用戶登入 - 使用專用客戶端
   */
  async login(email, password) {
    try {
      this.log(`嘗試登入: ${email}`);
      
      const response = await this.authClient.post('auth/login', {
        email,
        password
      });
      
      if (response.data && response.data.success) {
        this.log('登入成功, 保存用戶數據');
        
        const { token, user } = response.data.data;
        
        // 保存令牌
        this.saveToken(token);
        
        // 保存用戶資訊
        localStorage.setItem('user', JSON.stringify(user));
        
        // 保存公司 ID
        const companyId = user.companyId || user.company_id;
        if (companyId) {
          localStorage.setItem('companyId', String(companyId));
          this.log('保存公司 ID:', companyId);
        }
        
        return {
          success: true,
          data: response.data.data,
          user,
          token
        };
      } else {
        this.log('登入失敗:', response.data?.message);
        return {
          success: false,
          message: response.data?.message || '登入失敗'
        };
      }
    } catch (error) {
      console.error('登入錯誤:', error);
      
      let errorMessage = '登入請求失敗';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = '無法連接到服務器，請檢查網絡連接';
      }
      
      return {
        success: false,
        message: errorMessage,
        error: error.message
      };
    }
  }

  /**
   * 🔥 修復：用戶登出 - 使用專用客戶端
   */
  async logout() {
    try {
      this.log('發送登出請求到伺服器');
      
      // 嘗試向後端發送登出請求
      await this.authClient.post('auth/logout');
      this.log('伺服器登出請求成功');
    } catch (error) {
      console.warn('登出請求失敗:', error.message);
      // 即使請求失敗，也要清除本地數據
    } finally {
      this.log('清除本地認證數據');
      
      // 清除所有可能的令牌存儲
      const keysToRemove = [
        'authToken', 'token', 'accessToken', 'jwt', 'jwtToken',
        'user', 'companyId', 'devToken',
        'agencySelectedCompanyId', 'agencySelectedCompanyName'
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      return {
        success: true,
        message: '登出成功'
      };
    }
  }

  /**
   * 檢查用戶權限
   */
  hasRole(roles) {
    const user = this.getCurrentUser();
    if (!user) return false;

    if (!roles) return true;
    
    const requiredRoles = Array.isArray(roles) ? roles : [roles];
    const hasPermission = requiredRoles.includes(user.role);
    
    this.log('權限檢查:', {
      userRole: user.role,
      requiredRoles,
      hasPermission
    });
    
    return hasPermission;
  }

  /**
   * 🔥 修復：刷新令牌 - 使用專用客戶端
   */
  async refreshToken() {
    try {
      this.log('嘗試刷新令牌');
      
      const response = await this.authClient.post('auth/refresh-token');
      
      if (response.data && response.data.success) {
        this.log('令牌刷新成功');
        this.saveToken(response.data.data.token);
        
        return {
          success: true,
          data: response.data.data
        };
      } else {
        this.log('令牌刷新失敗');
        return {
          success: false,
          message: response.data?.message || '刷新令牌失敗'
        };
      }
    } catch (error) {
      console.error('刷新令牌錯誤:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || '刷新令牌請求失敗',
        error: error.message
      };
    }
  }

  /**
   * 🔥 新增：驗證當前令牌是否有效
   */
  async validateToken() {
    try {
      const response = await this.authClient.get('auth/me');
      
      if (response.data && response.data.success) {
        this.log('令牌驗證成功');
        return true;
      }
      
      return false;
    } catch (error) {
      this.log('令牌驗證失敗:', error.message);
      return false;
    }
  }
}

// 🔥 修復：導出單例實例
const authService = new AuthService();
export default authService;